import {useEffect} from 'react'
import {useNavigateToQuery} from '../hooks/use-navigate-to-query'
import {useLocation} from '@github-ui/react-router'
import {RETRANSMIT_REACT_EVENT, SEARCH_EVENT, type SearchEventDetail} from '@github-ui/search-bar-events/events'
import {
  dispatchProvideFeedback,
  dispatchReactConnected,
  dispatchReactDisconnected,
  dispatchSetGlobalNavVisibility,
} from '@github-ui/search-bar-events'
import {updateSearchBar} from './utils/update-search-bar'

/**
 * This hook handles transmitting events between the search bar and this react app to
 * confirm that the app is mounted and to respond to searches initiated in the search bar.
 */
export function useSearchBarEvents(): void {
  // Setup React Search Bar
  useEffect(() => {
    // If the search bar renders after us, it will check if react is loaded. Need to respond
    // with the right custom event.
    function handleRetransmitReactConnection() {
      dispatchReactConnected()

      // Hide the global nav while in the search UI
      dispatchSetGlobalNavVisibility(false)
    }

    // Emit a custom event to tell the search bar that we're connected
    handleRetransmitReactConnection()
    window.addEventListener(RETRANSMIT_REACT_EVENT, handleRetransmitReactConnection)
    return () => {
      // Restore the global nav to the visible state
      dispatchSetGlobalNavVisibility(true)
      window.removeEventListener(RETRANSMIT_REACT_EVENT, handleRetransmitReactConnection)
      dispatchReactDisconnected()
    }
  }, [])

  // Handle Search
  const navigateToQuery = useNavigateToQuery()

  useEffect(() => {
    function handleSearchEvent(e: Event) {
      if (!(e instanceof CustomEvent)) return
      const {search, searchParams} = (e as CustomEvent<SearchEventDetail>).detail
      navigateToQuery(search, undefined, searchParams)
    }

    window.addEventListener(SEARCH_EVENT, handleSearchEvent)
    return () => {
      window.removeEventListener(SEARCH_EVENT, handleSearchEvent)
    }
  }, [navigateToQuery])

  const {search} = useLocation()
  const query = new URLSearchParams(search).get('q') ?? ''
  useEffect(() => {
    updateSearchBar(query)
  }, [query])
}

/**
 * Trigger the "provide feedback" dialog
 */
export function provideFeedback(): void {
  dispatchProvideFeedback()
}
